import React, { useState, useEffect } from 'react'

import { returnSegmentedData, returnPassword, entropyBits, strengthOf, meterWidth } from '../lib'

import { CopyOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons'

import { Segmented, Button, Typography } from 'antd'

const { Text } = Typography

// 🔴 A REFUSAL MUST NOT BREAK THE SITE — site standard, part 2, and this was a real defect.
// `window.gtag` does not exist until a visitor says yes; that is the gate's whole point. So
// calling it directly threw `window.gtag is not a function` as the FIRST statement of every
// handler below — and with cookies refused, Copy, Regenerate, the reveal toggle and both
// size controls all did nothing at all.
//
// Measured against production, 19 Sep 2026, with a control group:
//   refused  → choosing the 64 size left the password at 32 characters; reveal dead;
//              2 page errors
//   accepted → 32 became 64; reveal worked; no errors
//
// The gate defines `window.siteTrack` for exactly this, and it too exists only after a yes,
// so the optional call IS the fix: the event is sent when it can be, and the work happens
// either way. A site whose buttons stop working when somebody declines tracking is punishing
// the refusal, which is the one thing the copy promises it will not do.
const track = (name, params) => { window.siteTrack?.(name, params) }

// The strength ladder and the meter's scale live in `src/lib/passwordStrength.js`, because they
// are the one claim this site makes about a password and they have to be testable without a
// browser. They were three `if` lines here, with a 120-bit ceiling that made 32, 48 AND 64
// characters all read "Fortress".
const SegmentedUI = () => {
  const { lengths, symbols } = returnSegmentedData()

  const [size, setSize] = useState(32)
  const [useSymbols, setUseSymbols] = useState(true)
  const [password, setPassword] = useState('')
  const [revealed, setRevealed] = useState(false)
  const [copiedMsg, setCopiedMsg] = useState(null)

  useEffect(() => {
    setPassword(returnPassword({ size, symbols: useSymbols }))
  }, [size, useSymbols])

  const bits = entropyBits(size, useSymbols)
  const strength = strengthOf(bits)

  const doCopy = async (text, msg) => {
    let ok = false
    try {
      await navigator.clipboard.writeText(text)
      ok = true
    } catch (e) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try { ok = document.execCommand('copy') } catch (e2) { ok = false }
      document.body.removeChild(ta)
    }
    setCopiedMsg(ok ? msg : null)
    if (ok) setTimeout(() => setCopiedMsg(null), 2500)
  }

  const handleCopy = () => {
    track('password_copied', { event_category: 'Password', event_label: String(size) })
    doCopy(password, 'Copied to clipboard')
  }

  const handleRegenerate = () => {
    track('password_regenerated', { event_category: 'Password', event_label: String(size) })
    const next = returnPassword({ size, symbols: useSymbols })
    setPassword(next)
    doCopy(next, 'New password copied')
  }

  return (
    <div className='dvs-vault'>
      {copiedMsg && <div className='dvs-copied'>{copiedMsg}</div>}
      <div className='dvs-password'>
        <Text
          className='dvs-password-text'
          style={{ fontSize: password.length > 48 ? 15 : password.length > 30 ? 18 : 22 }}
        >
          {revealed ? password : '•'.repeat(password.length)}
        </Text>
        <button className='dvs-icon-btn' onClick={() => { track('password_revealed', { event_category: 'UI', event_label: revealed ? 'hide' : 'show' }); setRevealed(!revealed) }} aria-label='Toggle password visibility'>
          {revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        </button>
      </div>

      <div className='dvs-meter'>
        <div className='dvs-meter-bar'>
          <div className={`dvs-meter-fill level-${strength.level}`} style={{ width: `${meterWidth(bits)}%` }} />
        </div>
        <div className='dvs-meter-meta'>
          <span>{strength.label}</span>
          <span>~{bits} bits of entropy</span>
        </div>
      </div>

      <div className='dvs-controls'>
        <div className='dvs-control'>
          <span className='dvs-control-label'>Size</span>
          <Segmented options={lengths.map(l => ({ label: String(l.value), value: l.value }))} value={size} onChange={value => { track('password_size_changed', { event_category: 'Settings', event_label: String(value) }); setSize(value) }} />
        </div>
        <div className='dvs-control'>
          <span className='dvs-control-label'>Symbols</span>
          <Segmented options={symbols.map(s => ({ label: s.value ? 'Yes' : 'No', value: s.value }))} value={useSymbols} onChange={value => { track('password_symbols_changed', { event_category: 'Settings', event_label: value ? 'on' : 'off' }); setUseSymbols(value) }} />
        </div>
      </div>

      <div className='dvs-actions'>
        <Button type='primary' shape='round' size='large' icon={<CopyOutlined />} onClick={handleCopy}>
          Copy
        </Button>
        <Button shape='round' size='large' icon={<ReloadOutlined />} onClick={handleRegenerate}>
          Regenerate
        </Button>
      </div>
    </div>
  )
}

export default SegmentedUI
